import { NgTemplateOutlet, AsyncPipe, NgIf, NgFor, DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, inject, input, contentChildren, TemplateRef, computed, Component, Injectable, APP_INITIALIZER, DestroyRef } from '@angular/core';
import { ReplaySubject, startWith, distinctUntilChanged, shareReplay, switchMap, BehaviorSubject, map, scan, catchError, of, filter, take, fromEvent } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
const _c0 = ["interpolate", ""];
function InterpolateComponent_For_1_Conditional_0_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function InterpolateComponent_For_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtemplate(1, InterpolateComponent_For_1_Conditional_0_ng_container_1_Template, 1, 0, "ng-container", 0);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const token_r1 = i0.ɵɵnextContext().$implicit;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.templates()[token_r1.index]);
  }
}
function InterpolateComponent_For_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const token_r1 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(token_r1.text);
  }
}
function InterpolateComponent_For_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, InterpolateComponent_For_1_Conditional_0_Template, 2, 1, "span")(1, InterpolateComponent_For_1_Conditional_1_Template, 2, 1, "span");
  }
  if (rf & 2) {
    const token_r1 = ctx.$implicit;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵconditional(token_r1.type === "placeholder" && ctx_r1.templates()[token_r1.index] ? 0 : 1);
  }
}
var TokenType;
(function (TokenType) {
  TokenType["Text"] = "text";
  TokenType["Placeholder"] = "placeholder";
})(TokenType || (TokenType = {}));
const PlaceholderToken = (text, index) => {
  return {
    type: TokenType.Placeholder,
    text,
    index
  };
};
const TextToken = text => {
  return {
    type: TokenType.Text,
    text,
    value: text
  };
};

/**
 * Parses `template` using `regexp` to build tokens.
 *
 * @param template Template string using zero-indexed tokens
 * @param regexp A regular expression that matches tokens; default is for
 *   csharp-style. The first capture group should capture the digits.
 * @returns {Token[]}
 */
const createTokenizer = regexp => template => {
  // copy the template to sever any references
  let remainingTemplate = template.slice();
  const tokens = [];
  if (regexp.global) {
    let match;
    while ((match = regexp.exec(template)) !== null) {
      if (match.length < 2) {
        throw Error('The regular expression must have at least one capture group.');
      }
      const matchStr = match[0],
        matchVal = Number(match[1]);
      // get the index of the match
      const matchPos = remainingTemplate.indexOf(matchStr);
      // gather the string up to the match
      // if matchIdx is 0, segment will be the empty string
      const priorSegment = remainingTemplate.slice(0, matchPos);
      // copy any non-empty segments into the tokens array
      if (priorSegment.length > 0) {
        tokens.push(TextToken(priorSegment));
      }
      // push the placeholder; the value is parsed to a number so that it can be used
      // to access templates by index
      tokens.push(PlaceholderToken(matchStr, matchVal));
      // advance tempString past this match.
      remainingTemplate = template.slice(regexp.lastIndex);
    }
  } else {
    throw Error('createTokenizer(template) requires a global Regular Expression. Pass the `g` flag.');
  }
  // there might be some text left over in tempString, which should be added as a token
  if (remainingTemplate.length > 0) {
    tokens.push(TextToken(remainingTemplate));
  }
  return tokens;
};
const cSharpPlaceholderRegExp = /\{(\d+)\}/g;
const csharpTokenizer = createTokenizer(cSharpPlaceholderRegExp);
const TOD_TOKENIZER = new InjectionToken('TOD_TOKENIZER', {
  providedIn: 'root',
  factory: () => csharpTokenizer
});
class InterpolateComponent {
  constructor() {
    this.tokenize = inject(TOD_TOKENIZER);
    this.interpolate = input.required();
    this.templates = contentChildren(TemplateRef);
    this.tokens = computed(() => this.tokenize(this.interpolate()));
  }
  static {
    this.ɵfac = function InterpolateComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || InterpolateComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: InterpolateComponent,
      selectors: [["", "interpolate", ""]],
      contentQueries: function InterpolateComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuerySignal(dirIndex, ctx.templates, TemplateRef, 4);
        }
        if (rf & 2) {
          i0.ɵɵqueryAdvance();
        }
      },
      inputs: {
        interpolate: [1, "interpolate"]
      },
      standalone: true,
      features: [i0.ɵɵStandaloneFeature],
      attrs: _c0,
      decls: 2,
      vars: 0,
      consts: [[4, "ngTemplateOutlet"]],
      template: function InterpolateComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵrepeaterCreate(0, InterpolateComponent_For_1_Template, 2, 1, null, null, i0.ɵɵrepeaterTrackByIndex);
        }
        if (rf & 2) {
          i0.ɵɵrepeater(ctx.tokens());
        }
      },
      dependencies: [NgTemplateOutlet],
      encapsulation: 2
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(InterpolateComponent, [{
    type: Component,
    args: [{
      // eslint-disable-next-line @angular-eslint/component-selector
      selector: '[interpolate]',
      standalone: true,
      imports: [AsyncPipe, NgTemplateOutlet, NgIf, NgFor],
      template: `
        @for (token of tokens(); track $index) {
            @if (token.type === 'placeholder' && templates()[token.index]) {
                <span>
                    <ng-container
                        *ngTemplateOutlet="templates()[token.index]"
                    ></ng-container>
                </span>
            } @else {
                <span>{{ token.text }}</span>
            }
        }
    `
    }]
  }], null, null);
})();
/**
 * @deprecated Prefer the InterpolateComponent
 */
const TranslateComponent = InterpolateComponent;

/**
 * Builds tokens from strings using sprintf-style placeholder tokens.
 *
 * @param template Template string with sprintf-style tokens
 * @returns {Token[]} Array of tokens that can be used to rebuild the string.
 */
function sprintfTokenizer(template) {
  const tokens = [];
  const regexp = /%((\d+)\$)?(f|d|s|u)/g;
  // some matches don't have numbers in them
  // the common behavior is to keep an index of those
  // tokens separately, and then bind to their index
  let unnumberedTokenIdx = 0;
  let match;
  let remainingTemplate = template.slice();
  while ((match = regexp.exec(template)) !== null) {
    const placeholder = match[0];
    const placeholderIdx = match[2] ? parseInt(match[2], 10) - 1 : unnumberedTokenIdx;
    // get the index of the match
    const matchPos = remainingTemplate.indexOf(placeholder);
    // gather the string up to the match
    // if pos is 0, segment will be the empty string
    const priorSegment = remainingTemplate.slice(0, matchPos);
    // copy any non-empty segments into the tokens array
    if (priorSegment.length > 0) {
      tokens.push(TextToken(priorSegment));
    }
    // push the placeholder; the value is parsed to a number so that it can be used
    // to access templates by index
    tokens.push(PlaceholderToken(placeholder, placeholderIdx));
    // advance tempString past this match.
    remainingTemplate = template.slice(regexp.lastIndex);
    // if this was an unnumbered token, advance the internal counter.
    if (!match[2]) {
      unnumberedTokenIdx++;
    }
  }
  // there might be some text left over in tempString, which should be added as a token
  if (remainingTemplate.length > 0) {
    tokens.push(TextToken(remainingTemplate));
  }
  return tokens;
}
var TodLogLevel;
(function (TodLogLevel) {
  TodLogLevel[TodLogLevel["None"] = 0] = "None";
  TodLogLevel[TodLogLevel["Error"] = 1] = "Error";
  TodLogLevel[TodLogLevel["Warn"] = 2] = "Warn";
  TodLogLevel[TodLogLevel["Info"] = 3] = "Info";
  TodLogLevel[TodLogLevel["Debug"] = 4] = "Debug";
  TodLogLevel[TodLogLevel["Trace"] = 5] = "Trace";
})(TodLogLevel || (TodLogLevel = {}));
const TodLogLevelLabel = {
  [TodLogLevel.None]: 'None',
  [TodLogLevel.Error]: 'Error',
  [TodLogLevel.Warn]: 'Warn',
  [TodLogLevel.Info]: 'Info',
  [TodLogLevel.Debug]: 'Debug',
  [TodLogLevel.Trace]: 'Trace'
};
const TOD_LOG_LEVEL = new InjectionToken('TOD_LOG_LEVEL', {
  factory: () => TodLogLevel.Warn
});
const debugEnabled = logLevel => logLevel >= TodLogLevel.Debug;
const traceEnabled = logLevel => logLevel >= TodLogLevel.Trace;
const TOD_DEFAULT_LOCALE = new InjectionToken('TOD_DEFAULT_LOCALE', {
  factory: () => 'en-US'
});
const TOD_JOIN_CHAR = new InjectionToken('TOD_JOIN_CHAR', {
  factory: () => '*'
});
const TOD_URL_LENGTH_LIMIT = new InjectionToken('TOD_URL_LENGTH_LIMIT', {
  factory: () => 2000
});

//let $console = TaggedConsole('TranslationService');
const TOD_URL_TEMPLATE = new InjectionToken('TOD_URL_TEMPLATE');

// TranslationService uses a client-server model to fetch translations as needed from a server.
class TranslationService {
  constructor() {
    this.http = inject(HttpClient);
    /**
     * Interpolated string used to build URLs for the GET requests to the ToD
     * server.
     */
    this.urlTemplate = inject(TOD_URL_TEMPLATE);
    this.urlLengthLimit = inject(TOD_URL_LENGTH_LIMIT);
    this.logLevel = inject(TOD_LOG_LEVEL);
    this.localeSubject = new ReplaySubject(1);
    this.locale$ = this.localeSubject.asObservable().pipe(startWith(inject(TOD_DEFAULT_LOCALE)), distinctUntilChanged(), shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    this._joinChar = inject(TOD_JOIN_CHAR);
  }
  /**
   * @param urlTemplate A string containing a URL with two optional tokens. {0}
   *   will be replace with the current locale. {1} will be replaced with the
   *   current Unix epoch, suitable for cache busting
   * @returns
   */
  setUrlTemplate(urlTemplate) {
    this.urlTemplate = urlTemplate;
    return this;
  }
  /**
   * Sets the locale, which will trigger application-wide translation updates.
   *
   * @param locale A locale identifier, such as 'en-US' or 'es-419'
   * @returns TranslationService instance
   */
  setLocale(locale) {
    this.localeSubject.next(locale);
    return this;
  }
  /**
   *
   * @param locale BCP 47 language tag (e.g. 'en-US', 'es-419')
   * @param labelKeys array of label keys to fetch from the ToD server. If null, fetch the entire dictionary.
   * @returns
   */
  getLabels(locale, labelKeys = null) {
    const queryParamName = 'labels';
    const url = new URL(this.urlTemplate.split('{0}').join(locale).split('{1}').join(Date.now().toString()));
    if (labelKeys && labelKeys.length > 0) {
      const joined = labelKeys.join(this.joinChar);
      const urlLength = url.toString().length + queryParamName.length + joined.length + 2;
      if (urlLength > this.urlLengthLimit) {
        if (debugEnabled(this.logLevel)) {
          // eslint-disable-next-line no-console
          console.debug(`URL length ${urlLength} exceeds limit ${this.urlLengthLimit}. Sending POST request.`);
        }
        return this.http.post(url.toString(), {
          labels: labelKeys
        });
      }
      url.searchParams.set(queryParamName, joined);
    }
    return this.http.get(url.toString());
  }
  /**
   * The character used to join label keys in the URL query string.
   *
   * @returns The character used to join label keys in the URL query string.
   */
  get joinChar() {
    return this._joinChar;
  }
  /**
   * @param value The character used to join label keys in the URL query string.
   * @returns void
   */
  set joinChar(value) {
    if (value.length !== 1) {
      throw new Error(`TranslationService.joinChar must be a single character.`);
    }
    this._joinChar = value;
  }
  /**
   * @param labelKeys An array of labels to subscribe to as the locale changes.
   *   If null, this will request the entire untrimmed dictionary.
   * @param prefix
   * @param suffix
   * @returns
   */
  translateKeys(labelKeys = null) {
    if (traceEnabled(this.logLevel)) {
      // eslint-disable-next-line no-console
      console.log('translateKeys', {
        labelKeys
      }, {
        localeSubject: this.localeSubject
      });
    }
    return this.locale$.pipe(switchMap(locale => this.getLabels(locale, labelKeys)));
  }
  static {
    this.ɵfac = function TranslationService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || TranslationService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: TranslationService,
      factory: TranslationService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TranslationService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
const provideFakeTranslationOnDemand = labels => {
  const localeSubject = new BehaviorSubject('en-US');
  const labelKeys = Object.keys(labels);
  return [{
    provide: TranslationService,
    useValue: {
      setLocale: locale => localeSubject.next(locale),
      locale$: localeSubject.asObservable(),
      translateKeys: () => localeSubject.pipe(map(locale => labelKeys.reduce((acc, key) => {
        acc[key] = locale + ' ' + labels[key];
        return acc;
      }, {})))
    }
  }];
};
const useTranslationObservable = fallbackLabels => inject(TranslationService).translateKeys(Object.keys(fallbackLabels)).pipe(startWith(fallbackLabels), scan((oldLabels, newLabels) => ({
  ...oldLabels,
  ...newLabels
}), {}), catchError(() => of(fallbackLabels)));
const useTranslationSignal = initialValue => {
  return toSignal(useTranslationObservable(initialValue), {
    initialValue
  });
};
const withLabelEndpoint = urlTemplate => {
  return providers => {
    return {
      before: [{
        provide: TOD_URL_TEMPLATE,
        useValue: urlTemplate
      }, ...providers.before],
      after: providers.after
    };
  };
};
const withUrlLengthLimit = urlLengthLimit => {
  return orderedProviders => {
    return {
      before: [...orderedProviders.before, {
        provide: TOD_URL_LENGTH_LIMIT,
        useValue: urlLengthLimit
      }],
      after: orderedProviders.after
    };
  };
};
const withLogging = logLevel => {
  return orderedProviders => {
    return {
      before: [...orderedProviders.before, {
        provide: TOD_LOG_LEVEL,
        useValue: logLevel
      }],
      after: orderedProviders.after
    };
  };
};
const withJoinChar = joinChar => {
  return orderedProviders => {
    return {
      before: [...orderedProviders.before, {
        provide: TOD_JOIN_CHAR,
        useValue: joinChar
      }],
      after: orderedProviders.after
    };
  };
};
const withDefaultLocale = defaultLocale => {
  return orderedProviders => {
    return {
      before: [{
        provide: TOD_DEFAULT_LOCALE,
        useValue: defaultLocale
      }, ...orderedProviders.before],
      after: orderedProviders.after
    };
  };
};
const provideTranslationOnDemand = (...setupFns) => {
  const {
    before,
    after
  } = setupFns.reduce((acc, fn) => fn(acc), {
    before: [],
    after: []
  });
  return [...before, TranslationService, ...after];
};
const withLocaleFromQueryParam = (paramName, once = false) => {
  return providers => {
    return {
      before: [...providers.before],
      after: [...providers.after, {
        provide: APP_INITIALIZER,
        deps: [TranslationService, Router, DestroyRef],
        useFactory: (i18n, r, x) => () => {
          void r.events.pipe(filter(event => event instanceof NavigationEnd), map(() => {
            let route = r.routerState.root;
            while (route.firstChild) {
              route = route.firstChild;
            }
            return route;
          }), switchMap(route => route.queryParamMap), map(params => params.get(paramName)), filter(locale => locale != null && typeof locale === 'string'), once ? take(1) : takeUntilDestroyed(x)).subscribe(locale => i18n.setLocale(locale));
        },
        multi: true
      }]
    };
  };
};
const withLocaleFromMessageEvent = (predicate, project = data => `${data}`) => {
  return orderedProviders => {
    return {
      before: [...orderedProviders.before],
      after: [...orderedProviders.after, {
        provide: APP_INITIALIZER,
        deps: [DOCUMENT, DestroyRef, TranslationService],
        useFactory: (d, x, i18n) => () => {
          const w = d.defaultView;
          if (w) {
            fromEvent(w, 'message').pipe(filter(predicate), map(event => event.data), map(project), takeUntilDestroyed(x))
            // eslint-disable-next-line rxjs/no-ignored-subscription
            .subscribe(locale => i18n.setLocale(locale));
          }
        },
        multi: true
      }]
    };
  };
};
const TOD_ON_FALLBACK_MISSING = new InjectionToken('TOD_ON_FALLBACK_MISSING');
const TOD_ON_LOCALE_MISSING = new InjectionToken('TOD_ON_LOCALE_MISSING');

// components
//   interpolate

/**
 * Generated bundle index. Do not edit.
 */

export { InterpolateComponent, PlaceholderToken, TOD_DEFAULT_LOCALE, TOD_JOIN_CHAR, TOD_LOG_LEVEL, TOD_ON_FALLBACK_MISSING, TOD_ON_LOCALE_MISSING, TOD_TOKENIZER, TOD_URL_LENGTH_LIMIT, TOD_URL_TEMPLATE, TextToken, TodLogLevel, TodLogLevelLabel, TokenType, TranslateComponent, TranslationService, cSharpPlaceholderRegExp, createTokenizer, csharpTokenizer, provideFakeTranslationOnDemand, provideTranslationOnDemand, sprintfTokenizer, useTranslationObservable, useTranslationSignal, withDefaultLocale, withJoinChar, withLabelEndpoint, withLocaleFromMessageEvent, withLocaleFromQueryParam, withLogging, withUrlLengthLimit };
